import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { PhoneInput, parsePhone, formatFullPhone } from '@/components/ui/phone-input'
import { ArrowLeft } from 'lucide-react'
import type { PageProps, Client } from '@/types'

interface ClientFormProps extends PageProps {
  client: Client | null
  errors?: Record<string, string>
}

export default function ClientForm({
  client,
  errors = {},
  current_user,
  current_organization,
  current_business,
}: ClientFormProps) {
  const orgSlug = current_organization?.slug || ''
  const isEditing = !!client
  const parsed = parsePhone(client?.phone || '')

  const [processing, setProcessing] = useState(false)
  const [name, setName] = useState(client?.name || '')
  const [email, setEmail] = useState(client?.email || '')
  const [countryCode, setCountryCode] = useState(parsed.countryCode)
  const [localPhone, setLocalPhone] = useState(parsed.localPhone)
  const [whatsappOptedIn, setWhatsappOptedIn] = useState(client?.whatsapp_opted_in ?? true)
  const [notes, setNotes] = useState(client?.notes || '')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setProcessing(true)
    const formData = {
      client: {
        name,
        email,
        phone: formatFullPhone(countryCode, localPhone),
        whatsapp_opted_in: whatsappOptedIn,
        notes,
      }
    }

    if (isEditing) {
      router.put(`/${orgSlug}/clients/${client.id}`, formData, {
        onFinish: () => setProcessing(false),
      })
    } else {
      router.post(`/${orgSlug}/clients`, formData, {
        onFinish: () => setProcessing(false),
      })
    }
  }

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <Link
            href={`/${orgSlug}/clients`}
            className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver a clientes
          </Link>
          <h1 className="text-2xl font-semibold text-foreground">
            {isEditing ? 'Editar cliente' : 'Nuevo cliente'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nombre completo</Label>
              <Input
                id="name"
                placeholder="Juan Pérez"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Teléfono (WhatsApp)</Label>
              <PhoneInput
                countryCode={countryCode}
                localPhone={localPhone}
                onCountryCodeChange={setCountryCode}
                onLocalPhoneChange={setLocalPhone}
              />
              {errors.phone && (
                <p className="text-sm text-destructive">{errors.phone}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Correo electrónico (opcional)</Label>
              <Input
                id="email"
                type="email"
                placeholder="juan@ejemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {errors.email && (
                <p className="text-sm text-destructive">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notas (opcional)</Label>
              <textarea
                id="notes"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder="Preferencias, alergias, etc."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="whatsapp_opted_in"
                className="h-4 w-4 rounded border-input"
                checked={whatsappOptedIn}
                onChange={(e) => setWhatsappOptedIn(e.target.checked)}
              />
              <Label htmlFor="whatsapp_opted_in" className="font-normal">
                Acepta recibir notificaciones por WhatsApp
              </Label>
            </div>
          </div>

          <div className="flex gap-3">
            <Button type="submit" disabled={processing}>
              {processing
                ? 'Guardando...'
                : isEditing
                  ? 'Guardar cambios'
                  : 'Crear cliente'}
            </Button>
            <Link href={`/${orgSlug}/clients`}>
              <Button type="button" variant="outline">
                Cancelar
              </Button>
            </Link>
          </div>
        </form>
      </div>
    </DashboardLayout>
  )
}
